import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  loginApi,
  meApi,
  logoutApi,
  signupApi,
  VerifyOTPApi,
} from "../api/authAPI";
import {
  clearAccessToken,
  getAccessToken,
  setAccessToken,
} from "../state/tokenStore";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import type { SignUpData, User } from "../types";
import { isLoggedin, removeToken, saveToken } from "../lib/isLoggedin";
import { clearAllCache } from "./swrCache";
import logger from "../utils/logger";

type UserLocation = {
  lat: any;
  lon: any;
} | null;
type AuthCtx = {
  user: User;
  token: string | null;
  loading: boolean;
  court: {
    state: number;
    district: number;
    complex: number;
    courtRoom: number;
  };
  userLocation: UserLocation;
  setUser: React.Dispatch<React.SetStateAction<User>>;
  setCourts: React.Dispatch<
    React.SetStateAction<{
      state: number;
      district: number;
      complex: number;
      courtRoom: number;
    }>
  >;
  login: (
    email: string,
    password: string,
    forceLogin?: boolean,
    redirectTo?: string | null,
  ) => Promise<void>;
  otpVerify: (data: {
    phone: string;
    otp: string;
    purpose: string;
    redirectTo?: string | null;
  }) => Promise<any>;
  logout: () => Promise<void>;
  signUpUser: (data: SignUpData) => Promise<any>;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setUserLocation: React.Dispatch<React.SetStateAction<any>>;
  updateUser: (updatedData: any) => void;
  me: () => Promise<void>;
};

const AuthContext = createContext<AuthCtx | undefined>(undefined);

const getCookieToken = () => {
  const cookieName = btoa("token").replace(/=+$/, "");

  return document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${cookieName}=`))
    ?.split("=")[1];
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User>(null);
  const [token, setToken] = useState<string | null>(
    () => getCookieToken() || null,
  );
  const [loading, setLoading] = useState(true);
  const [userLocation, setUserLocationState] = useState<UserLocation>(null);
  const meInFlightRef = useRef<Promise<void> | null>(null);
  const lastMeKeyRef = useRef("");
  const didBootstrapMeRef = useRef(false);
  let [court, setCourts] = useState({
    state: 0,
    district: 0,
    complex: 0,
    courtRoom: 0,
  });
  const navigate = useNavigate();

  const setUserLocation: React.Dispatch<React.SetStateAction<any>> =
    useCallback((next) => {
      setUserLocationState((prev: any) => {
        const value = typeof next === "function" ? next(prev) : next;
        if (
          prev &&
          value &&
          String(prev.lat ?? "") === String(value.lat ?? "") &&
          String(prev.lon ?? "") === String(value.lon ?? "") &&
          String(prev.displayName ?? "") === String(value.displayName ?? "") &&
          String(prev.source ?? "") === String(value.source ?? "")
        ) {
          return prev;
        }

        return value;
      });
    }, []);

  useLayoutEffect(() => {
    const cookieToken = getCookieToken();
    if (cookieToken && cookieToken !== token) {
      setToken(cookieToken);
      setAccessToken(cookieToken);
    }
  }, [token]);

  const me = useCallback(async () => {
    const cookieToken = getCookieToken();
    const memoryToken = getAccessToken();

    if (cookieToken && cookieToken !== memoryToken) {
      setAccessToken(cookieToken);
      setToken(cookieToken);
    }

    if (!cookieToken && !memoryToken && !isLoggedin()) {
      setLoading(false);
      return;
    }

    const meKey = [
      cookieToken || memoryToken || "stored",
      userLocation?.lat ?? "",
      userLocation?.lon ?? "",
    ].join(":");

    if (meInFlightRef.current && lastMeKeyRef.current === meKey) {
      return meInFlightRef.current;
    }

    lastMeKeyRef.current = meKey;
    const request = (async () => {
      try {
        const res = await meApi({
          latitude: userLocation?.lat,
          longitude: userLocation?.lon,
        });
        let data = res?.data;
        if (data?.status) {
          setUser(data?.user);
        } else {
          clearAccessToken();
          setToken(null);
          setUser(null);
          removeToken();
          if (data?.message) toast.error(data.message);
        }
      } catch (e: any) {
        clearAccessToken();
        setToken(null);
        setUser(null);
        removeToken();
        logger.error("Error fetching user data:", e);
        // const message = e.response?.data?.message || e.message;
        // if (message && e.response?.status !== 401) toast.error(message);
        const status = e?.response?.status;
        const message = e?.response?.data?.message || e?.message;

        if (status !== 401 && status !== 403 && message) {
          toast.error(message);
        }
      } finally {
        setLoading(false);
        meInFlightRef.current = null;
      }
    })();

    meInFlightRef.current = request;
    return request;
  }, [userLocation?.lat, userLocation?.lon]);

  useEffect(() => {
    if (didBootstrapMeRef.current) return;
    didBootstrapMeRef.current = true;
    me();
  }, []);

  const updateUser = (updatedData: any) => {
    setUser((prev: any) => ({ ...prev, ...updatedData }));
    me();
  };

  const deleteAllCookies = () => {
    const cookies = document.cookie.split(";");
    logger.log("cookies", cookies);
    document.cookie.split(";")?.forEach((cookie) => {
      const name = cookie.split("=")[0].trim();
      document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;`;
    });
  };

  const login = async (
    username: string,
    password: string,
    forceLogin = false,
    redirectTo?: string | null,
  ): Promise<any> => {
    clearAllCache();
    try {
      const rememberedUsername = localStorage.getItem("rememberedUsername");
      localStorage.clear();
      if (rememberedUsername) {
        localStorage.setItem("rememberedUsername", rememberedUsername);
      }
      deleteAllCookies();
      const res = await loginApi({ username: username, password, forceLogin });
      let data = res?.data;
      // if (data.status || data.requiresOTP) {
      //   toast.success(res.data.message);
      //   setLoading(false);
      //   navigate("/otp-verification", {
      //     state: {
      //       type: data.passwordExpired ? "forgot" : "signin",
      //       dialCode: data.dialCode,
      //       phone: data.phone,
      //     },
      //   });
      // } else {
      //   toast.error(res.data.message);
      // }
      // session already exists
      if (data?.showSessionPopup) {
        return data;
      }

      // normal login success
      if (data.status || data.requiresOTP) {
        toast.success(res.data.message);

        setLoading(false);

        navigate("/otp-verification", {
          state: {
            type: data.passwordExpired ? "forgot" : "signin",
            dialCode: data.dialCode,
            phone: data.phone,
            firstTimeLogin: data.firstTimeLogin,
            cooldownSeconds: data.cooldownSeconds,
            redirectTo,
          },
        });

        return data;
      } else {
        toast.error(res.data.message);
        return data;
      }
    } catch (e: any) {
      logger.error(e);
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const signUpUser = async (data: SignUpData) => {
    try {
      const res = await signupApi(data);
      if (res?.data.status || res?.data.requiresOTP) {
        toast.success(res.data.message);
        setLoading(false);
        navigate("/otp-verification?", {
          state: {
            type: "signup",
            dialCode: data.dialCode,
            phone: data.phone,
            cooldownSeconds: res.data.cooldownSeconds,
          },
        });
      } else {
        toast.error(res.data.message);
      }
      return res;
    } catch (e: any) {
      toast.error(e?.message);
    } finally {
      setLoading(false);
    }
  };

  const otpVerify = async ({
    phone,
    otp,
    purpose,
    redirectTo,
  }: {
    phone: string;
    otp: string;
    purpose: string;
    redirectTo?: string | null;
  }) => {
    try {
      setLoading(true);
      const res = await VerifyOTPApi({ phone, otp, purpose });
      let data = res?.data;

      if (data?.status) {
        if (data?.firstTimeLogin) {
          toast.success(data?.message || "OTP verified successfully");

          navigate("/set-password", {
            state: {
              phone,
            },
          });

          return data;
        }
        let token = data?.token;
        if (purpose === "signup") {
          toast.success("Signup successful! Please log in.");
          navigate("/");
          return;
        } else if (token && purpose === "signin") {
          setAccessToken(token);
          setToken(token);
          toast.success(res?.data?.message || "OTP verified successfully");
          saveToken(token);
          await me();
          navigate(redirectTo || "/dashboard");
        } else if (purpose === "forgot") {
          toast.success(res?.data?.message || "OTP verified successfully");
          navigate(`/set-password`, {
            state: { phone, dialCode: data.dialCode },
          });
        }
      } else {
        toast.error(data?.message || "OTP verification failed");
      }
      return res;
    } catch (e: any) {
      logger.error(e);
      toast.error(
        e.message || e.response.data.message || "Something went wrong",
      );
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    clearAllCache();
    try {
      let res = await logoutApi();
      let data = res?.data;
      if (data?.status) {
        toast.success(data?.message || "Logged out successfully");
      }
    } finally {
      localStorage.removeItem("rememberedUsername");
      clearAccessToken();
      setToken(null);
      setUser(null);
      removeToken();
      navigate("/");
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        setUser,
        setLoading,
        login,
        otpVerify,
        logout,
        signUpUser,
        court,
        setCourts,
        userLocation,
        setUserLocation,
        updateUser,
        me,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
